import { useAuth0 } from "@auth0/auth0-react";
import MenuIcon from "@mui/icons-material/Menu";
import { Drawer as MuiDrawer, styled, Theme, useMediaQuery, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { InfoResponse } from "@/@types/growthos-api/schema/openapi";
import ArrowRight from "@/Components/Icons/ArrowRight";
import BrandEquity from "@/Components/Icons/BrandEquity";
import { Campaign } from "@/Components/Icons/Campaign";
// Icons
import { DataOS } from "@/Components/Icons/DataOS";
import FinancialReview from "@/Components/Icons/FinancialReview";
import { FlightingIcon } from "@/Components/Icons/FlightingIcon";
import Formats from "@/Components/Icons/Formats";
import { Geo } from "@/Components/Icons/Geo";
import { LC } from "@/Components/Icons/LC";
import { Logout } from "@/Components/Icons/Logout";
import MROI from "@/Components/Icons/MROI";
import Publishers from "@/Components/Icons/Publishers";
import Rocket from "@/Components/Icons/Rocket";
import Sales from "@/Components/Icons/Sales";
import SatCurve from "@/Components/Icons/SatCurve";
import { SB } from "@/Components/Icons/SB";
import SettingsIcon from "@/Components/Icons/Settings";
import { SparklesIcon } from "@/Components/Icons/SparklesIcons";
import { User } from "@/Components/Icons/User";
import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { List } from "@/Components/v2/atoms/List";
import { Menu } from "@/Components/v2/atoms/Menu";
import { IconMenuItem } from "@/Components/v2/atoms/MenuItem";
import { Stack } from "@/Components/v2/atoms/Stack";
import { LinkType } from "@/Components/v2/constants/enum";
import { DrawerAccountSelector } from "@/Components/v2/molecule/DrawerAccountSelector";
import { DrawerFooter } from "@/Components/v2/molecule/DrawerFooter/DrawerFooter";
import { DrawerHeader } from "@/Components/v2/molecule/DrawerHeader/DrawerHeader";
import { SettingMenuItem } from "@/Components/v2/molecule/SettingMenuItem";
import { ThemeToggle } from "@/Components/v2/molecule/ThemeToggle";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { EnvVar } from "@/env";
import { useLogout } from "@/hooks/useLogout";
import { colors } from "@/Utils";
import { DRAWER_BREAKPOINT, DRAWER_WIDTH_PX } from "@/Utils/GlobalStyle";
import { hasAccess } from "@/Utils/ShowComponent";

import MenuGroup from "./MenuGroup";

const SUBHEADER_TEXT = "GrowthOS: Marketing Decision Engine";

interface MenuListItem {
  id: string;
  label: string;
  route: string;
  activePaths: string[];
  onClick?: () => void;
  hasAccess: boolean;
  isBeta?: boolean;
  type?: LinkType;
  icon?: React.ReactNode;
}

export interface MenuListGroup {
  id: string;
  label: string;
  items: MenuListItem[];
}

enum DrawerType {
  Permanent = "permanent",
  Temporary = "temporary",
  Persistant = "persistant",
}

const StyledDrawer = styled(MuiDrawer)(({ theme }) => {
  return {
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: DRAWER_WIDTH_PX,
      padding: "32px 21px 12px",
      borderRight: "none",
      boxSizing: "border-box",
      // Set background color to #131313 only in dark mode; use default in light mode
      backgroundColor: theme.palette.mode === "dark" ? colors.bodyMain : "",
      backgroundImage: "none",
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "transparent",
    },
  };
});

const StyleIconButton = styled(IconButton)(() => {
  return {
    borderRadius: "4px",
    border: "1px solid #DFE0E3",
  };
});

const StyledSparkesIcon = styled(SparklesIcon)(({ theme }) => {
  return {
    fill: theme.palette.brandedTypography.textColor,
  };
});

const StyledSBIcon = styled(SB)(({ theme }) => {
  return {
    fill: theme.palette.brandedTypography.textColor,
  };
});

const getDataOSUrl = (auth0OrgId: string) => {
  switch (EnvVar.envName) {
    case "dev":
      return `https://dev.dataos.mutinex.co/login/?organization=${auth0OrgId}`;
    case "stg":
      return `https://staging.dataos.mutinex.co/login/?organization=${auth0OrgId}`;
    default:
      return `https://dataos.mutinex.co/login/?organization=${auth0OrgId}`;
  }
};

export const Drawer: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);
  const isLargerScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up(DRAWER_BREAKPOINT));
  const { loginWithPopup } = useAuth0();
  const { logout } = useLogout();
  const variant = isLargerScreen ? DrawerType.Permanent : DrawerType.Temporary;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleSettingsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const location = useLocation();
  const { value: isScenarioBuilderV3Enabled } = useFeatureGate(featureFlag.scenarioBuilderV3);
  const { value: isGeographiesEnabled } = useFeatureGate(featureFlag.geographies);
  const { value: isCampaignInsightsEnabled } = useFeatureGate(featureFlag.campaignInsights);
  const { value: isMaiteChatHistoryEnabled } = useFeatureGate(featureFlag.maiteChatHistory);
  const { value: isMaiteV2Enabled } = useFeatureGate(featureFlag.maiteV2Enabled);
  const { value: isBrandEquityEnabled } = useFeatureGate(featureFlag.brandEquity);
  const { value: isGrowthCentreEnabled } = useFeatureGate(featureFlag.growthCentre);
  const theme = useTheme();

  const {
    loading,
    currentOrganization,
    organizationMemberships: orgMemberships,
    userData: user,
    switchAccount,
  } = useInfo();

  const organizationMemberships = useMemo(() => {
    const isAuth0OrgIdSupplied = Boolean(EnvVar.auth0OrgId);
    if (isAuth0OrgIdSupplied) {
      return [];
    }

    return [...orgMemberships].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [orgMemberships]);

  const auth0OrgId = currentOrganization?.customerProviderId;
  const hendrenTier = currentOrganization?.configuration?.hendrenTier;
  const dataOsLoginUrl = getDataOSUrl(auth0OrgId || "");
  const intercomLearningCenter = "https://help.growthos.mutinex.co/";

  const menuListGroups: MenuListGroup[] = [
    ...(isGrowthCentreEnabled
      ? [
          {
            id: "growth-center",
            label: "",
            items: [
              {
                id: "growth-center-home",
                label: "Growth Center",
                route: "/growth-center",
                activePaths: ["/growth-center"],
                hasAccess: hasAccess(user, "Viewer"),
                icon: <Rocket fontSize="small" />,
                type: LinkType.Internal,
              },
            ],
          },
        ]
      : []),
    ...(hendrenTier !== "OFF"
      ? [
          {
            id: "maite",
            label: "AI Consultant",
            items: [
              {
                id: "maite-chat",
                label: "MAITE",
                route: "/maite",
                activePaths: ["/maite"],
                icon: <StyledSparkesIcon fontSize="small" width={20} />,
                hasAccess: hasAccess(user, "Viewer") && isMaiteChatHistoryEnabled,
                type: LinkType.Internal,
                isBeta: false,
              },
              {
                id: "maite-chat-v2",
                label: "MAITE",
                route: "/maite-v2",
                activePaths: ["/maite-v2"],
                icon: <StyledSparkesIcon fontSize="small" width={20} />,
                hasAccess: hasAccess(user, "Viewer") && isMaiteV2Enabled,
                type: LinkType.Internal,
                isBeta: false,
              },
            ],
          },
        ]
      : []),
    {
      id: "media-decisions",
      label: "Media Decisions",
      items: [
        {
          id: "dashboard",
          label: "Channel ROI",
          route: "/channel-roi",
          activePaths: ["/channel-roi"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <MROI fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "investment-levels-v3",
          label: "Investment Levels",
          route: "/investment-levels",
          activePaths: ["/investment-levels"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <SatCurve fontSize="small" />,
          type: LinkType.Internal,
        },
        {
          id: "flighting",
          label: "Flighting",
          route: "/flighting",
          activePaths: ["/flighting"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <FlightingIcon fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "geographies",
          label: "Geographies",
          route: "/geographies",
          activePaths: ["/geographies"],
          icon: <Geo fontSize="small" />,
          hasAccess: hasAccess(user, "Viewer") && isGeographiesEnabled,
          type: LinkType.Internal,
        },

        {
          id: "campaigns",
          label: "Campaigns",
          route: "/campaigns",
          activePaths: ["/campaigns"],
          hasAccess: hasAccess(user, "Viewer") && isCampaignInsightsEnabled,
          icon: <Campaign fontSize="small" />,
          type: LinkType.Internal,
        },
      ],
    },
    {
      id: "sales-analysis",
      label: "Sales Analysis",
      items: [
        {
          id: "sales-drivers",
          label: "Sales Drivers",
          route: "/sales-drivers",
          activePaths: ["/sales-drivers"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <Sales fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "financial-review",
          label: "Financial Review",
          route: "/financial-review",
          activePaths: ["/financial-review"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <FinancialReview fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "brand-equity",
          label: "Brand Equity",
          route: "/brand-equity",
          activePaths: ["/brand-equity"],
          hasAccess: hasAccess(user, "Viewer") && isBrandEquityEnabled,
          icon: <BrandEquity fontSize="small" />,
          type: LinkType.Internal,
        },
      ],
    },
    {
      id: "channel-analysis",
      label: "Channel Analysis",
      items: [
        {
          id: "publishers",
          label: "Publishers",
          route: "/publishers",
          activePaths: ["/publishers"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <Publishers fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "formats",
          label: "Formats",
          route: "/formats",
          activePaths: ["/formats"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <Formats fontSize="small" />, // change later
          type: LinkType.Internal,
        },
      ],
    },
    {
      id: "strategy-planning",
      label: "Strategy & Planning",
      items: [
        {
          id: "scenario-builder",
          label: "Scenario Builder",
          route: isScenarioBuilderV3Enabled ? "/scenarios-v3" : "/scenarios-v2",
          activePaths: isScenarioBuilderV3Enabled ? ["/scenarios-v3"] : ["/scenarios-v2"],
          icon: <StyledSBIcon fontSize="small" />,
          hasAccess: hasAccess(user, "Analyst"),
          type: LinkType.Internal,
        },
      ],
    },
  ];

  const footerMenuItems: MenuListGroup[] = [
    {
      id: "footer-items",
      label: "",
      items: [
        {
          id: "learning-center",
          label: "GrowthLabs",
          icon: <LC fontSize="small" />,
          route: intercomLearningCenter,
          activePaths: [intercomLearningCenter],
          hasAccess: hasAccess(user, "Viewer"),
          type: LinkType.External,
        },
      ],
    },
  ];

  const settingsMenuTopItems: MenuListItem[] = [
    {
      id: "team-management",
      label: "Team Management",
      route: "/team-management",
      activePaths: ["/team-management"],
      icon: <User fontSize="small" />,
      hasAccess: hasAccess(user, "Admin"),
      type: LinkType.Internal,
    },
    {
      id: "data-os",
      label: "DataOS",
      route: dataOsLoginUrl,
      activePaths: [dataOsLoginUrl],
      hasAccess: hasAccess(user, "Viewer"),
      icon: <DataOS fontSize="small" />,
      type: LinkType.External,
    },
  ];

  const settingsMenuBottomItems: MenuListItem[] = [
    {
      id: "logout",
      label: "Log Out",
      route: "#",
      activePaths: [],
      onClick: logout,
      icon: <Logout fontSize="small" />,
      hasAccess: true,
      type: LinkType.Internal,
    },
  ];

  if (loading) {
    return null;
  }

  const handleAccountSelected = async (org: InfoResponse["currentOrganization"]) => {
    await loginWithPopup({
      authorizationParams: {
        organization: org.customerProviderId,
        ignoreCache: true,
      },
    });

    switchAccount(org);
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setIsOpen(newOpen);
  };

  const isSelected = (routes: string[]) => {
    return routes.includes(location.pathname);
  };

  return (
    <Box position="relative">
      <Box position="fixed" top={20} left={30}>
        <StyleIconButton
          onClick={toggleDrawer(true)}
          aria-label="open drawer"
          edge="start"
          color="inherit"
          sx={{
            width: 50,
            height: 50,
          }}
          data-testcomp="OpenDrawer"
        >
          <MenuIcon sx={{ fontSize: 30 }} />
        </StyleIconButton>
      </Box>
      <StyledDrawer
        open={isOpen}
        onClose={toggleDrawer(false)}
        variant={variant}
        sx={
          variant === DrawerType.Permanent
            ? {
                "& .MuiDrawer-paper": {
                  boxShadow: "0px 4px 4px 0px #00000040",
                },
              }
            : {}
        }
      >
        <DrawerHeader
          subheader={SUBHEADER_TEXT}
          toggleDrawer={toggleDrawer(false)}
          hideIcon={isLargerScreen}
        />
        {currentOrganization && (
          <DrawerAccountSelector
            selectedAccount={currentOrganization}
            accounts={organizationMemberships}
            onAccountSelected={handleAccountSelected}
          />
        )}
        <List>
          {menuListGroups.map((group, index) => {
            return <MenuGroup key={index} group={group} isSelected={isSelected} />;
          })}
        </List>
        <DrawerFooter>
          {footerMenuItems.map((group) => {
            return <MenuGroup key={group.id} group={group} isSelected={isSelected} />;
          })}
          <IconMenuItem
            aria-controls={open ? "settings-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            icon={<SettingsIcon sx={{ color: theme.palette.brandedTypography.textColor }} />}
            onClick={handleSettingsClick as () => void}
          >
            <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
              <span>Settings</span> <ArrowRight />
            </Stack>
          </IconMenuItem>
        </DrawerFooter>
        <Menu
          elevation={3}
          sx={{
            padding: 0,
            marginTop: 0,
            "& .MuiMenu-list": {
              padding: "0",
            },
            "& .MuiMenu-paper": {
              borderRadius: "12px",
            },
          }}
          id="settings-menu"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={open}
          onClose={handleSettingsClose}
        >
          <Stack>
            {settingsMenuTopItems.map((item) => {
              return (
                <SettingMenuItem key={item.id} selected={isSelected(item.activePaths)} {...item} />
              );
            })}
            <ThemeToggle />
            {settingsMenuBottomItems.map((item) => {
              return (
                <SettingMenuItem key={item.id} selected={isSelected(item.activePaths)} {...item} />
              );
            })}
          </Stack>
        </Menu>
      </StyledDrawer>
    </Box>
  );
};

export default Drawer;
